import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { marqueeFrameworks } from "../../mock/frameworks";
import { SectionLabel } from "../shared";

const stats = [
  { n: "73+", label: "Risk frameworks" },
  { n: "6", label: "US regulator hubs" },
  { n: "87+", label: "Interactive tools" },
  { n: "212+", label: "Glossary terms" }
];

export default function HeroSection() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#F3EEE4] to-[#FAF7F1] pointer-events-none" />
      <div className="relative max-w-[1320px] mx-auto px-5 lg:px-8 pt-16 lg:pt-24 pb-14">
        <div className="max-w-3xl rp-fade-up">
          <SectionLabel>AI · Risk Management · Encyclopedia</SectionLabel>
          <h1 className="font-display font-bold text-[42px] leading-[1.05] sm:text-[58px] lg:text-[72px] tracking-tight text-[#1A1914]">
            Match the right <span className="text-[#3F4CE0]">risk framework.</span><br />In under <span className="text-[#E8590C]">two minutes.</span>
          </h1>
          <p className="mt-6 text-[17px] lg:text-[19px] leading-relaxed text-[#1A1914]/65 max-w-2xl">
            RiskPedia USA is the encyclopedia and AI advisor for American risk professionals. Answer five questions about your industry, maturity and goals — get a tailored shortlist of frameworks with <strong className="text-[#1A1914]">Sam</strong>, an AI chat advisor, on standby.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup" data-testid="hero-get-started" className="inline-flex items-center gap-2 px-6 py-3.5 text-[15px] font-semibold text-white bg-[#3F4CE0] hover:bg-[#3341C6] rounded-xl rp-btn shadow-md">
              Get started — it's free <ArrowRight size={16} />
            </Link>
            <Link to="/library" data-testid="hero-browse-library" className="inline-flex items-center gap-2 px-6 py-3.5 text-[15px] font-semibold text-[#1A1914] bg-white border border-[#E7E0D2] hover:border-[#1A1914]/40 rounded-xl rp-btn">
              Browse the library
            </Link>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E7E0D2] border border-[#E7E0D2] rounded-2xl overflow-hidden rp-fade-up rp-delay-2" data-testid="hero-stats">
          {stats.map((s) => (
            <div key={s.label} className="bg-[#FAF7F1] px-6 py-6 hover:bg-white transition-colors duration-300">
              <p className="font-display font-bold text-[34px] lg:text-[40px] text-[#1A1914]">{s.n}</p>
              <p className="mt-1 text-[13px] text-[#1A1914]/55">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Marquee */}
      <div className="relative border-y border-[#E7E0D2] bg-white/60 py-4 overflow-hidden" data-testid="framework-marquee">
        <p className="font-mono-label text-[10px] text-[#1A1914]/40 text-center mb-3">A curated index</p>
        <div className="flex whitespace-nowrap rp-marquee-track w-max">
          {[...marqueeFrameworks, ...marqueeFrameworks].map((f, i) => (
            <span key={i} className="mx-2 inline-flex px-3.5 py-1.5 text-[13px] font-medium text-[#1A1914]/70 bg-[#F3EEE4] rounded-full border border-[#E7E0D2]">{f}</span>
          ))}
        </div>
        <p className="text-center mt-3">
          <Link to="/library" className="text-[13px] text-[#3F4CE0] hover:underline inline-flex items-center gap-1">Don't see what you need? Suggest a framework <ArrowRight size={13} /></Link>
        </p>
      </div>

      {/* Newsletter strip */}
      <div id="newsletter" className="max-w-[1320px] mx-auto px-5 lg:px-8 py-10">
        <div className="rounded-2xl border border-[#E7E0D2] bg-white px-6 py-6 md:px-8 flex flex-col md:flex-row md:items-center gap-4 justify-between rp-card">
          <div>
            <h3 className="font-display font-semibold text-[20px] flex items-center gap-2"><Sparkles size={18} className="text-[#12B76A]" /> US Risk Intelligence — straight to your inbox</h3>
            <p className="mt-1 text-[13.5px] text-[#1A1914]/55">Join 2,900+ American risk professionals. Every Monday. Free. Unsubscribe anytime.</p>
          </div>
          <NewsletterForm />
        </div>
      </div>
    </section>
  );
}

function NewsletterForm() {
  const [email, setEmail] = React.useState("");
  const [done, setDone] = React.useState(false);
  const [err, setErr] = React.useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    try {
      const { api } = await import("../../lib/api");
      await api.post("/newsletter", { email });
      setDone(true);
    } catch (ex) {
      if (ex.response?.status === 409) setErr("You're already subscribed!");
      else setErr("Something went wrong — try again.");
    }
  };
  if (done) return <p className="text-[14px] font-semibold text-[#0E9355]" data-testid="newsletter-success">You're in! First briefing lands Monday.</p>;
  return (
    <form onSubmit={submit} className="flex flex-col gap-1.5 w-full md:w-auto">
      <div className="flex gap-2">
        <input type="email" required value={email} onChange={(e) => { setEmail(e.target.value); setErr(""); }} placeholder="you@organization.gov" data-testid="newsletter-email"
          className="flex-1 md:w-72 px-4 py-2.5 text-[14px] border border-[#E7E0D2] rounded-xl bg-[#FAF7F1] focus:outline-none focus:ring-2 focus:ring-[#12B76A]/40 focus:border-[#12B76A]" />
        <button type="submit" data-testid="newsletter-submit" className="px-5 py-2.5 text-[14px] font-semibold text-white bg-[#12B76A] hover:bg-[#0E9355] rounded-xl rp-btn">Subscribe free</button>
      </div>
      {err && <p className="text-[12px] text-[#DC2626]" data-testid="newsletter-error">{err}</p>}
    </form>
  );
}
